import { Dialog } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useT } from "~/lib/i18n";
import { cn } from "~/lib/cn";
import { Button } from "./button";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** A composer rather than buttons (the assistant): no separating line, an even margin around it. */
  footerVariant?: "actions" | "composer";
  size?: "md" | "lg";
}

/**
 * Side panel on desktop, full-height bottom sheet on phones. Used for creation
 * and editing so the underlying list keeps its scroll position and filters.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  footerVariant = "actions",
  size = "md",
}: SheetProps) {
  const t = useT();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-[2px] transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0" />
        <Dialog.Popup
          className={cn(
            "fixed z-50 flex flex-col bg-surface text-fg shadow-pop outline-none transition-transform duration-250 ease-[cubic-bezier(.2,.8,.2,1)]",
            "inset-x-0 bottom-0 top-[max(env(safe-area-inset-top),2.5rem)] rounded-t-[16px] data-ending-style:translate-y-full data-starting-style:translate-y-full",
            "md:inset-y-2 md:right-2 md:left-auto md:top-2 md:rounded-panel md:data-ending-style:translate-x-[105%] md:data-ending-style:translate-y-0 md:data-starting-style:translate-x-[105%] md:data-starting-style:translate-y-0",
            size === "lg" ? "md:w-[min(760px,calc(100vw-1rem))]" : "md:w-[min(520px,calc(100vw-1rem))]",
          )}
        >
          <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line-strong md:hidden" aria-hidden />
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 pt-4 pb-3 md:px-6 md:pt-5">
            <div className="min-w-0">
              <Dialog.Title className="font-display text-lg font-medium">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-0.5 text-sm text-fg-2">{description}</Dialog.Description> : null}
            </div>
            <Dialog.Close render={<Button variant="ghost" size="icon-sm" aria-label={t("common.actions.close")} />}>
              <X aria-hidden />
            </Dialog.Close>
          </header>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 md:px-6">{children}</div>
          {footer ? (
            // The bottom padding also clears a phone's home bar: `safe-bottom` alone would replace it by 0.
            <footer
              className={cn(
                "flex items-center justify-end gap-2 px-5 md:px-6",
                footerVariant === "composer"
                  ? "pt-2 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:pb-6"
                  : "border-t border-line pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]",
              )}
            >
              {footer}
            </footer>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
